import { isEmpty } from 'lodash-es';
import type { Airflow } from '@aviata/platform-types';
import type { ProductsState, ProductState } from '@aviata/products-fns';
import type { PassengersAncillaryState } from '@aviata/ancillary-fns';

import type { SelectedSeatsState } from '@/widgets/seat-map';

import { BloomreachEventName } from './config';
import { bloomreachLib } from '../bloomreach';
import type { ProductsPageOpenParams, BookingSuccessInfo } from '../analytics';
import { extractAnalyticsDataLib } from '../analytics-data-extraction';

const getProductsData = (meta: ProductsPageOpenParams) => {
  const hasAncillaries = meta.isBaggageAvailable || meta.isSeatMapAvailable;

  const packageTypes = meta.packages?.map(pkg => pkg.type);
  // Deduplicate product names to avoid multiple entries for products like roaming (one per passenger)
  const productNames = meta.products
    ? [...new Set(meta.products.map(product => product.service))]
    : undefined;

  const data = {
    available_ancillaries: hasAncillaries ? [] : undefined,
    available_packages: isEmpty(packageTypes) ? undefined : packageTypes,
    available_products: isEmpty(productNames) ? undefined : productNames,
  };

  if (hasAncillaries) {
    if (meta.isSeatMapAvailable) {
      data.available_ancillaries.push('seat_map');
    }
    if (meta.isBaggageAvailable) {
      data.available_ancillaries.push('baggage');
    }
  }

  return data;
};

export const revenuePageOpen = (params: ProductsPageOpenParams) => {
  bloomreachLib.track(
    BloomreachEventName.REVENUE_PAGE_OPEN,
    getProductsData(params),
  );
};

type ProductsStateEntry = [Airflow.Products.Service, ProductState];

const getProductsList = (
  products: ProductsState | null,
  mapFn: ([key, value]: ProductsStateEntry) => string | undefined,
) => {
  if (products) {
    const productsList = (Object.entries(products) as ProductsStateEntry[]).map(
      mapFn,
    );
    const filteredList = productsList.filter(Boolean);

    return filteredList.length > 0 ? filteredList : undefined;
  }
};

export const successBooking = (
  info: BookingSuccessInfo & { isAuthorized: boolean },
) => {
  let productsNotInPackage;
  let packageProducts;

  if (info.products) {
    packageProducts = getProductsList(
      info.products,
      ([productName, productState]) => {
        if (productState.id && productState.selectedAsPackage) {
          return productName;
        }
      },
    );

    productsNotInPackage = getProductsList(
      info.products,
      ([productName, productState]) => {
        if (productState.id && !productState.selectedAsPackage) {
          return productName;
        }
      },
    );
  }

  if (info.roaming) {
    if (productsNotInPackage) {
      // Only add roaming if it's not already in the list (may exist as a key in products state)
      if (!productsNotInPackage.includes('roaming')) {
        productsNotInPackage.push('roaming');
      }
    } else {
      productsNotInPackage = ['roaming'];
    }
  }

  const baggageCount = extractAnalyticsDataLib.baggageCount(info.baggage);
  const seatMapCount = extractAnalyticsDataLib.seatsCount(info.seatMap);

  bloomreachLib.track(BloomreachEventName.SUCCESS_BOOKING, {
    is_authorized: info.isAuthorized,
    ancillaries_baggage_count: baggageCount || 0,
    ancillaries_seat_map_count: seatMapCount,
    package_products: packageProducts,
    package_type: info.packageInfo?.type || undefined,
    products: productsNotInPackage,
    order_id: info.orderId,
    order_number: info.orderNumber,
    price: Number(info.total),
    // Override segments data and baggage flags from current offer if provided
    // (may be updated after fare family change)
    // This ensures baggage/hand_luggage info and has_baggage/has_hand_luggage flags are up-to-date
    ...(info.segmentsData && {
      segments_data_from: info.segmentsData.segments_data_from,
      segments_data_to: info.segmentsData.segments_data_to,
      from_dep: info.segmentsData.from_dep,
      from_arr: info.segmentsData.from_arr,
      to_dep: info.segmentsData.to_dep,
      to_arr: info.segmentsData.to_arr,
      ...extractAnalyticsDataLib.getBaggageFlagsFromSegmentsData(
        info.segmentsData,
      ),
    }),
  });
};
